import { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { images } from 'utils/icons';
import { ICON_TEXT_TYPE, MAP_CONFIG } from 'utils/constants/Map';

function getMarkerIcon({ seq, iconSeqFillColor = '#929598', iconTextType }) {
  let svg;
  switch (iconTextType) {
    case ICON_TEXT_TYPE.number:
      svg = `${'<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="200" height="200" viewBox="0 0 100 100">' +
        '<defs> <circle id="path-1" cx="50" cy="50" r="50"/><mask id="mask-2" width="30" height="30" x="0" y="0" fill="white">' +
        '<use xlink:href="#path-1"/> </mask> </defs> <g id="Page-1" fill="none" fill-rule="evenodd"> <g id="Phone-Portrait---320" transform="translate(-209 -51)">' +
        `<g id="Group" transform="translate(209 51)"> <use id="Circle" fill="${iconSeqFillColor}" stroke="#FFFFFF" stroke-width="10" mask="url(#mask-2)" xlink:href="#path-1"/>` +
        '<text id="1" fill="#FFFFFF" font-family="Source Sans Pro" font-size="48" font-weight="400" text-anchor="middle" x="50%" y="60%">'}${seq}</text> </g> </g> </g> </svg>`;
      break;

    case ICON_TEXT_TYPE.fillOnly:
      svg =
        `${'<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="200" height="200" viewBox="0 0 100 100">' +
          '<defs> <circle id="path-1" cx="50" cy="50" r="50"/><mask id="mask-2" width="30" height="30" x="0" y="0" fill="white">' +
          '<use xlink:href="#path-1"/> </mask> </defs> <g id="Page-1" fill="none" fill-rule="evenodd"> <g id="Phone-Portrait---320" transform="translate(-209 -51)">' +
          `<g id="Group" transform="translate(209 51)"> <use id="Circle" fill="${iconSeqFillColor}" stroke="#FFFFFF" stroke-width="10" mask="url(#mask-2)" xlink:href="#path-1"/>`}` +
        '</g> </g> </g> </svg>';
      break;

    default:
      svg =
        '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="200" height="200" viewBox="0 0 100 100">' +
        '<defs> <circle id="path-1" cx="50" cy="50" r="50"/><mask id="mask-2" width="30" height="30" x="0" y="0" fill="white">' +
        '<use xlink:href="#path-1"/> </mask> </defs> <g id="Page-1" fill="none" fill-rule="evenodd"> <g id="Phone-Portrait---320" transform="translate(-209 -51)">' +
        '<g id="Group" transform="translate(209 51)"> <use id="Circle" fill="#FFFFFF" stroke="#000000" stroke-width="14" mask="url(#mask-2)" xlink:href="#path-1"/>' +
        '</g> </g> </g> </svg>';
      break;
  }

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const SingleMarker = ({
  maps,
  map,
  position,
  icon,
  text,
  markerDraggable,
  markerDragStart,
  markerDragEnd,
  markerClick: handleMarkerClick,
  iconOverlay,
  iconSelected,
  iconSeqFillColor,
  iconTextType,
}) => {
  const shape = {
    coords: [1, 1, 1, 20, 18, 20, 18, 1],
    type: 'poly',
  };

  const getIcon = ({
    icon,
    text,
    iconOverlay,
    iconSeqFillColor,
    iconTextType,
  }) => {
    if (icon) {
      if (iconOverlay) {
        return images[`${icon}Overlay`]?.default;
      } else {
        const iconType =
          typeof icon === 'string' && icon.includes('https') // images now comes from api in tasks
            ? icon
            : images[icon]?.default
            ? images[icon]?.default
            : '';
        return iconType;
      }
    } else {
      return getMarkerIcon({ seq: text, iconSeqFillColor, iconTextType });
    }
  };
  const getScaledSize = iconOverlay => {
    if (iconOverlay) {
      return new maps.Size(
        MAP_CONFIG.overlayMarker.scaledSize,
        MAP_CONFIG.overlayMarker.scaledSize,
      );
    } else {
      return new maps.Size(
        MAP_CONFIG.normalMarker.scaledSize,
        MAP_CONFIG.normalMarker.scaledSize,
      );
    }
  };
  const getAnchor = iconOverlay => {
    if (iconOverlay) {
      return new maps.Point(
        MAP_CONFIG.overlayMarker.anchor,
        MAP_CONFIG.overlayMarker.anchor,
      );
    } else {
      return new maps.Point(
        MAP_CONFIG.normalMarker.anchor,
        MAP_CONFIG.normalMarker.anchor,
      );
    }
  };

  const marker = new maps.Marker({
    position,
    maps,
    draggable: markerDraggable,
    icon: {
      url: getIcon({
        icon,
        text,
        iconOverlay,
        iconSeqFillColor,
        iconTextType:
          iconTextType || (Number.isInteger(text) ? 'number' : 'outlined'),
      }),
      anchor: getAnchor(iconOverlay),
      scaledSize: getScaledSize(iconOverlay),
      origin: new maps.Point(0, 0),
    },
    shape,
    zIndex: iconSelected
      ? MAP_CONFIG.overlayMarker.iconZIndex
      : iconOverlay
      ? MAP_CONFIG.overlayMarker.iconZIndex - 1
      : MAP_CONFIG.normalMarker.zIndex,
  });

  const manageDragEvent = useCallback(
    evt => {
      if (markerDragStart) {
        markerDragStart(evt);
      }
    },
    [markerDragStart],
  );

  const markerDragEndEvent = useCallback(
    evt => {
      if (markerDragEnd) {
        markerDragEnd(evt);
      }
    },
    [markerDragEnd],
  );

  const markerClick = useCallback(
    evt => {
      if (handleMarkerClick) {
        handleMarkerClick({ evt, position, icon });
      }
    },
    [handleMarkerClick, icon, position],
  );

  marker.setMap(map);

  useEffect(() => {
    const dragStartListener = maps.event.addListener(
      marker,
      'dragstart',
      manageDragEvent,
    );

    const dragEndListener = maps.event.addListener(
      marker,
      'dragend',
      markerDragEndEvent,
    );

    const markerClickListener = maps.event.addListener(
      marker,
      'click',
      markerClick,
    );

    return () => {
      maps.event.removeListener(dragStartListener);
      maps.event.removeListener(dragEndListener);
      maps.event.removeListener(markerClickListener);
      marker.setMap(null);
    };
  }, [
    position,
    marker,
    manageDragEvent,
    markerDragEndEvent,
    markerClick,
    maps.event,
  ]);

  return null;
};

SingleMarker.defaultProps = {
  type: 'basic',
  markerDraggable: false,
};

SingleMarker.propTypes = {
  type: PropTypes.oneOf(['text', 'basic']),
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.string,
  maps: PropTypes.object,
  map: PropTypes.object,
  position: PropTypes.object,
  markerDraggable: PropTypes.bool,
  markerDragStart: PropTypes.func,
  markerDragEnd: PropTypes.func,
  markerClick: PropTypes.func,
  iconOverlay: PropTypes.bool,
  iconSelected: PropTypes.bool,
  iconSeqFillColor: PropTypes.string,
  iconTextType: PropTypes.object,
};

export default SingleMarker;
